import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useDeleteNote, type NoteRow } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/trackEvent";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

// CSR-771 — Delete Note confirmation modal. Soft-delete on the server
// (sets deleted_at = NOW()); the row is preserved alongside its activity-log
// entry. Hard delete is forbidden in this feature.

interface DeleteNoteConfirmModalProps {
  note: NoteRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteNoteConfirmModal({
  note,
  open,
  onOpenChange,
}: DeleteNoteConfirmModalProps) {
  const deleteNote = useDeleteNote();
  const { toast } = useToast();

  const handleConfirm = (e: React.MouseEvent) => {
    e.preventDefault();
    deleteNote.mutate(
      {
        noteId: note.id,
        insuredId: note.insuredId,
        policyId: note.policyId,
      },
      {
        onSuccess: () => {
          toast({
            title: "Note Deleted",
            description: "The note has been removed from the timeline.",
          });
          onOpenChange(false);
          const elapsed =
            Date.now() - new Date(note.createdAt as unknown as string).getTime();
          trackEvent("Note Deleted", {
            note_id: note.id,
            time_since_creation_ms: elapsed,
            scope: note.policyId ? "policy" : "insured",
          });
        },
        onError: () => {
          toast({
            title: "Failed to delete note. Please try again.",
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-note-modal">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle
              className="w-5 h-5 text-destructive"
              aria-hidden="true"
            />
            Delete Note
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this note? This action cannot be
            undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={deleteNote.isPending}
            data-testid="delete-note-cancel"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={deleteNote.isPending}
            className={cn(buttonVariants({ variant: "destructive" }), "gap-2")}
            data-testid="delete-note-confirm"
          >
            {deleteNote.isPending && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
